import { ApolloError, FetchResult } from '@apollo/client';
import { useLogOperation } from '@investigator/features/user/queries';

// Configuration constants
const LOG_OPERATION_TIMEOUT_MS = 3000; // 3 second timeout for logging operations
const MAX_RETRY_ATTEMPTS = 2;
const RETRY_DELAY_MS = 500;

/**
 * Type definition for log operation response
 */
interface LogOperationResponse {
  logOperation: {
    body: {
      data?: unknown;
      message?: string;
      success?: boolean;
    };
    status_code: number;
  } | null;
}

/**
 * Debug logging helper - only logs in development
 * @param args - Arguments to log
 */
const dbg = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[useLogOperationCall]', ...args);
  }
};

/**
 * Creates a promise that resolves after a delay
 * @param ms - Milliseconds to delay
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Creates a promise that rejects after a timeout
 * @param ms - Milliseconds before timeout
 * @param operation - Name of operation for error message
 */
const createTimeoutPromise = <T>(ms: number, operation: string): Promise<T> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`${operation} timed out after ${ms}ms`));
    }, ms);
  });
};

/**
 * Custom hook for logging operations to the backend
 * Provides a robust logging function with timeout protection and retry logic
 * Used for audit logging and analytics
 *
 * @returns {Function} logOperationCall - Function to log operations
 */
export const useLogOperationCall = () => {
  const [logOperation] = useLogOperation({
    errorPolicy: 'all',
    // Prevent Apollo from caching these mutations
    fetchPolicy: 'no-cache',
  });

  /**
   * Logs an operation to the backend with retry logic and timeout protection
   *
   * @async
   * @param {string} eventSubCategory - Sub-category of the event (required)
   * @param {string} [eventCategory] - Main category of the event
   * @param {string} [data] - Additional data to log
   * @param {number} [attemptNumber=1] - Current retry attempt number
   * @returns {Promise<FetchResult<LogOperationResponse> | undefined>} Result of the log operation or undefined on failure
   *
   * @example
   * // Log a privacy policy acknowledgment
   * await logOperationCall('PRIVACY_POLICY_ACKNOWLEDGED', 'USER');
   *
   * @example
   * // Log with additional data
   * await logOperationCall('DETECTION_VIEWED', 'SECURITY', JSON.stringify({ detectionId: '123' }));
   */
  const logOperationCall = async (
    eventSubCategory: string,
    eventCategory?: string,
    data?: string,
    attemptNumber = 1,
  ): Promise<FetchResult<LogOperationResponse> | undefined> => {
    // Validate required parameters
    if (!eventSubCategory) {
      console.warn('[useLogOperationCall] eventSubCategory is required');
      return undefined;
    }

    const variables = {
      input: {
        data,
        event_category: eventCategory,
        event_sub_category: eventSubCategory,
      },
    };

    dbg(`Sending (attempt ${attemptNumber}/${MAX_RETRY_ATTEMPTS}):`, variables);

    try {
      // Create the log operation promise
      const operationPromise = logOperation({
        variables,
        // Prevent batching for critical operations
        context: {
          debounceKey: `log-${Date.now()}`,
        },
      });

      // Race between operation and timeout
      const res = await Promise.race([
        operationPromise,
        createTimeoutPromise<FetchResult<LogOperationResponse>>(
          LOG_OPERATION_TIMEOUT_MS,
          'Log operation',
        ),
      ]);

      dbg('Result:', { data: res?.data, errors: res?.errors });

      // Check for successful response
      if (res?.data?.logOperation) {
        const statusCode = res.data.logOperation.status_code;
        const success = res.data.logOperation.body?.success;

        if (statusCode >= 200 && statusCode < 300 && success) {
          dbg('Log operation successful');
          return res;
        } else {
          console.warn(
            '[useLogOperationCall] Log operation returned non-success status:',
            {
              statusCode,
              success,
              message: res.data.logOperation.body?.message,
            },
          );
        }
      } else if (res?.data?.logOperation === null) {
        dbg('logOperation returned null - likely a non-critical operation');
      }

      return res;
    } catch (error: unknown) {
      const isTimeout = (error as Error)?.message?.includes('timed out');
      const isNetworkError = error instanceof ApolloError && error.networkError;

      // Determine if we should retry
      const shouldRetry =
        attemptNumber < MAX_RETRY_ATTEMPTS &&
        (isTimeout ||
          isNetworkError ||
          (error as Error)?.message?.includes('fetch failed') ||
          (error as Error)?.message?.includes('NetworkError'));

      if (shouldRetry) {
        console.warn(
          `[useLogOperationCall] Attempt ${attemptNumber} failed, retrying...`,
          { message: (error as Error)?.message },
        );

        // Wait before retrying with exponential backoff
        await delay(RETRY_DELAY_MS * attemptNumber);

        // Recursive retry
        return logOperationCall(
          eventSubCategory,
          eventCategory,
          data,
          attemptNumber + 1,
        );
      }

      // Log error details for debugging
      console.error('[useLogOperationCall] Final error after retries:', {
        message: (error as Error)?.message,
        graphQLErrors: (error as ApolloError)?.graphQLErrors,
        networkError: (error as ApolloError)?.networkError,
        attemptNumber,
      });

      // For non-critical logging operations, we don't throw
      // This prevents logging failures from breaking the app
      if (
        eventSubCategory.includes('ACKNOWLEDGED') ||
        eventSubCategory.includes('VIEWED') ||
        eventCategory === 'USER'
      ) {
        dbg('Non-critical operation failed, continuing without throwing');
        return undefined;
      }

      // For critical operations, throw the error
      throw error;
    }
  };

  return logOperationCall;
};
